import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { FeaturedBadge, logFeaturedClick } from "./FeaturedBadge";
import Img from "./Img";

/**
 * Featured carousel — horizontal scrolling strip above the workspace grid.
 * Session 42 · Renders TWO disjoint sections:
 *   1) Featured Spaces
 *   2) Editor's Picks
 * Silently hides a section when it has no eligible inventory.
 */
export default function FeaturedCarousel({ source = "carousel" }) {
  const [sections, setSections] = useState({ featured: [], editors_pick: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/merchandising")
      .then((r) => {
        if (!cancelled) setSections({
          featured: r.data?.featured || [],
          editors_pick: r.data?.editors_pick || [],
        });
      })
      .catch(() => { if (!cancelled) setSections({ featured: [], editors_pick: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  const featured = sections.featured || [];
  const editorsPick = sections.editors_pick || [];
  if (!featured.length && !editorsPick.length) return null;

  return (
    <>
      {featured.length > 0 && (
        <_MerchSection
          title="Featured Spaces"
          eyebrow="MÉTIER · FEATURED"
          items={featured}
          source={`${source}_featured`}
          testId="featured-section"
        />
      )}
      {editorsPick.length > 0 && (
        <_MerchSection
          title="Editor's Picks"
          eyebrow="MÉTIER · EDITOR'S PICKS"
          items={editorsPick}
          source={`${source}_editors_pick`}
          testId="editors-pick-section"
        />
      )}
    </>
  );
}

function _MerchSection({ title, eyebrow, items, source, testId }) {
  // Session 58.7 · P1 · If only ONE featured item exists, render it in a
  // contained card (max ~640px wide) instead of stretching a single item
  // across a full carousel row. Two+ items keep the existing horizontal
  // scroll carousel treatment. Aspect ratio + gold border stay identical
  // so the FEATURED / EDITOR'S PICK polish is preserved.
  const isSolo = items.length === 1;
  return (
    <section
      className="featured-carousel"
      data-testid={testId}
      style={{ marginTop: 28, marginBottom: 14 }}
    >
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        marginBottom: 12, gap: 12,
      }}>
        <div>
          <span className="m-eyebrow" style={{ color: "var(--gold)" }}>{eyebrow}</span>
          <h3 style={{ margin: "4px 0 0", fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 500, letterSpacing: 0.2 }}>
            {title}<span style={{ color: "var(--gold)" }}>.</span>
          </h3>
        </div>
      </div>
      <div
        data-testid={`${testId}-items`}
        data-solo={isSolo ? "true" : "false"}
        style={isSolo ? {
          // Contained solo card — max 640px, centered on desktop, full-width on mobile.
          display: "grid",
          gridTemplateColumns: "minmax(0, 640px)",
          gap: 14,
          paddingBottom: 10,
        } : {
          // Multi-item carousel — original layout.
          display: "grid",
          gridAutoFlow: "column",
          gridAutoColumns: "minmax(280px, 1fr)",
          gap: 14,
          overflowX: "auto",
          paddingBottom: 10,
          scrollSnapType: "x mandatory",
        }}
      >
        {items.map((f) => {
          const e = f.entity || {};
          const linkTo = e.slug ? `/spaces/${e.slug}` : "#";
          const wsShape = {
            workspace_id: f.entity_id,
            location_id: f.entity_type === "venue" ? f.entity_id : null,
            slug: e.slug,
            featured: {
              is_featured: true,
              priority: f.priority,
              badge_label: f.badge_label,
              via: f.entity_type,
            },
          };
          return (
            <Link
              key={f._id_key || `${f.entity_type}:${f.entity_id}`}
              to={linkTo}
              className="featured-card"
              data-testid={`featured-carousel-item-${f.entity_id}`}
              onClick={() => logFeaturedClick({ workspace: wsShape, source })}
              style={{
                scrollSnapAlign: "start",
                textDecoration: "none",
                color: "inherit",
                background: "var(--navy-mid, #232b48)",
                border: "1px solid rgba(212,168,75,0.28)",
                borderRadius: 14,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                transition: "transform .18s ease, box-shadow .18s ease",
              }}
              onMouseEnter={(ev) => { ev.currentTarget.style.transform = "translateY(-3px)"; }}
              onMouseLeave={(ev) => { ev.currentTarget.style.transform = "translateY(0)"; }}
            >
              {e.img && (
                <div style={{ position: "relative", aspectRatio: "16/10", overflow: "hidden" }}>
                  <Img src={e.img} alt={e.name || "Featured"}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <div style={{ position: "absolute", top: 10, left: 10 }}>
                    <FeaturedBadge workspace={wsShape} size="sm" />
                  </div>
                </div>
              )}
              <div style={{ padding: "14px 16px 16px", color: "#faf8f3" }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, lineHeight: 1.1 }}>
                  {e.name || "Untitled"}
                </div>
                <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                  {[e.neighborhood, e.city].filter(Boolean).join(" · ")}
                </div>
                {e.type && (
                  <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6, letterSpacing: 0.16, textTransform: "uppercase" }}>
                    {e.type}
                  </div>
                )}
                <span style={{
                  display: "inline-block", marginTop: 12, color: "var(--gold)",
                  fontSize: 12, letterSpacing: 0.14, textTransform: "uppercase", fontWeight: 600,
                }}>View details →</span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
